import { OktaAuth } from '@okta/okta-auth-js';
import envConfig from '../config/config';
import {
  isEhrOidcAuthMode,
  isEhrOktaCallbackPath,
  setEhrOidcAuthMode,
} from './okta-auth-mode';

export const oktaConfig = envConfig.okta;

export const issuer = `${oktaConfig.baseUrl}/oauth2/${oktaConfig.issuer}`;

/**
 * Persist EHR redirect path as early as possible on the OAuth callback route so token refresh
 * keeps redirect_uri …/callback/ehr after an EHR launch (e.g. /?fromLogin=true).
 */
if (typeof window !== 'undefined' && isEhrOktaCallbackPath()) {
  setEhrOidcAuthMode();
}

/**
 * Resolve Account UI OIDC client_id and redirect_uri path for this browsing context.
 * EHR launches use …/callback/ehr; standard login uses …/callback (same Okta SPA).
 */
export function resolveAccountOidcOptions() {
  const useEhrRedirect =
    typeof window !== 'undefined' && (isEhrOidcAuthMode() || isEhrOktaCallbackPath());
  return {
    useEhr: useEhrRedirect,
    clientId: envConfig.authorizedClientIds.accountUi,
    redirectPath: useEhrRedirect ? '/callback/ehr' : '/callback',
  };
}

export function getAccountOidcRedirectUri() {
  return `${getOauthBrowsingContextOrigin()}${resolveAccountOidcOptions().redirectPath}`;
}

/** Sync OktaAuth options when switching to EHR mode after the SDK was created (e.g. embedded iframe before redirect). */
export function applyResolvedOidcOptionsToOktaAuth(oktaAuth) {
  const { clientId, redirectPath } = resolveAccountOidcOptions();
  oktaAuth.options.clientId = clientId;
  oktaAuth.options.redirectUri = `${getOauthBrowsingContextOrigin()}${redirectPath}`;
}

/**
 * Origin for OAuth redirect_uri and Okta-react restoreOriginalUri. When Account UI runs in a frame,
 * prefer the top browsing context so Okta returns to the correct window. Cross-origin parents
 * cannot be read (accessing top.location throws); fall back to this frame.
 */
export function getOauthBrowsingContextOrigin() {
  try {
    if (window.top != null && window.top !== window.self) {
      return window.top.location.origin;
    }
  } catch {
    // cross-origin top
  }
  return window.location.origin;
}

/** Reused popup name so repeated OAuth attempts target the same window. */
export const OAUTH_POPUP_WINDOW_NAME = 'tempus_oauth_login';

/**
 * Navigate to Okta authorize URL in the top window when embedded so OAuth is not stuck in an iframe.
 * When Epic blocks cross-origin top navigation, open a same-origin popup so Auth0/Okta login UI is
 * not framed (Auth0 sets frame-ancestors 'none').
 * @param {string} href
 * @param {Window} [browsingWindow] defaults to global window (override in tests / non-browser)
 */
export function assignOauthRedirectLocation(href, browsingWindow = typeof window !== 'undefined' ? window : undefined) {
  if (!browsingWindow) {
    return;
  }

  const assignInThisWindow = () => {
    browsingWindow.location.assign(href);
  };

  const openInPopup = () => {
    const popup = browsingWindow.open(href, OAUTH_POPUP_WINDOW_NAME);
    return popup != null;
  };

  if (browsingWindow.self === browsingWindow.top) {
    assignInThisWindow();
    return;
  }

  try {
    browsingWindow.top.location.assign(href);
    return;
  } catch {
    // Cross-origin parent (e.g. Epic): cannot navigate top.
  }

  if (openInPopup()) {
    return;
  }

  assignInThisWindow();
}

export {
  handOffEhrOauthPopupToOpener,
  handOffOauthPopupToOpener,
  handOffOauthPopupToOpenerByNavigation,
} from './ehr-oauth-handoff';

const initialOidc = resolveAccountOidcOptions();
const oktaAuthConfiguration = {
  issuer,
  clientId: initialOidc.clientId,
  redirectUri: `${getOauthBrowsingContextOrigin()}${initialOidc.redirectPath}`,
  setLocation: assignOauthRedirectLocation,
};

export const oktaAuthJSClient = new OktaAuth(oktaAuthConfiguration);

export const REDIRECT_STORAGE_KEY = 'oidc-redirect';

export const USER_SESSION_INFO = 'user-session-info';
// Initial page with just email field
export const IDP_DISCOVERY = 'idp-discovery';
// Login + Password Page
export const PRIMARY_AUTH = 'primary-auth';
